import { useTranslation } from 'react-i18next';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Eye, EyeToShootingType } from 'src/models/Eye.model';
import { QualityPrecheckEnum } from 'src/models/Grading.model';
import { I18NextNS } from 'src/models/Language.model';

import { useGetCurrentCaptureProcess } from 'src/hooks/camera/useGetCurrentCaptureProcess';

interface IImageStatusTextProps {
  imageUrl: string;
  quality?: string;
  hasQualityPrecheck?: boolean;
  isUploading?: boolean;
  isUploadMode?: boolean;
  eyeType?: Eye;
  hasCanceled?: boolean;
}

const ImageStatusText = ({
  imageUrl,
  quality,
  hasQualityPrecheck = true,
  isUploading = false,
  isUploadMode = false,
  eyeType,
  hasCanceled = false,
}: IImageStatusTextProps) => {
  const { t } = useTranslation(I18NextNS.PAGES);

  const { isCanceling } = useRemoteBoundStore();

  const { currentCaptureProcess } = useGetCurrentCaptureProcess();

  if (isUploading) {
    return (
      <>
        <strong>{t('newExam.imageStatus.uploadingImage')}</strong>
        <br />
        {t('newExam.imageStatus.uploadingImageDesc')}
      </>
    );
  }

  if (quality === QualityPrecheckEnum.Usable) {
    return (
      <>
        <strong>{t('newExam.imageStatus.qualityUsable')}</strong>
        <br />
        {t('newExam.imageStatus.qualityUsableDesc')}
      </>
    );
  }

  if (quality === QualityPrecheckEnum.Reject) {
    return (
      <>
        <strong>{t('newExam.imageStatus.qualityFailed')}</strong>
        <br />
        {t('newExam.imageStatus.qualityFailedDesc')}
      </>
    );
  }

  if (hasQualityPrecheck) {
    return (
      <>
        <strong>{t('newExam.imageStatus.qualityAnalysis')}</strong>
        <br />
        {t('newExam.imageStatus.qualityAnalysisDesc')}
      </>
    );
  }

  if (
    isCanceling &&
    eyeType &&
    currentCaptureProcess?.take === EyeToShootingType[eyeType]
  ) {
    return (
      <>
        <strong>{t('newExam.imageStatus.cancelingImaging')}</strong>
      </>
    );
  }

  if (hasCanceled && !imageUrl) {
    return (
      <>
        <strong>{t('newExam.imageStatus.canceled')}</strong>
        <br />
        {t('newExam.imageStatus.canceledDesc')}
      </>
    );
  }

  if (!imageUrl) {
    return isUploadMode ? (
      <>
        <strong>{t('newExam.imageStatus.noImageUploaded')}</strong>
        <br />
        {t('newExam.imageStatus.noImageUploadedDesc')}
      </>
    ) : (
      <>
        <strong>{t('newExam.imageStatus.captureInProgress')}</strong>
        <br />
        {t('newExam.imageStatus.captureInProgressDesc')}
      </>
    );
  }

  return isUploadMode ? (
    <>
      <strong>{t('newExam.imageStatus.uploadedSuccessfully')}</strong>
      <br />
      {t('newExam.imageStatus.uploadedSuccessfullyDesc')}
    </>
  ) : (
    <>
      <strong>{t('newExam.imageStatus.capturedSuccessfully')}</strong>
      <br />
      {t('newExam.imageStatus.capturedSuccessfullyDesc')}
    </>
  );
};

export default ImageStatusText;
